/* Core & libraries */
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import csvDownload from "json-to-csv-export";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import {
  TRAVEL_CREDITS_GRID_ENDPOINT,
  UNASSIGN_CREDITS_ENDPOINT,
  TRAVELCREDITS_DOWNLOAD_ENDPOINT,
  TRAVELCREDITS_INITIAL_VIEW,
  TRIP_CREDIT_SETTINGS,
  GET_COMPANY_ATTRIBUTES,
  ASSIGN_TRAVEL_CREDITS_ENDPOINT,
  TRAVEL_CREDIT_COUNT_ENDPOINT
} from "../endpoints/endpoints";
import { useUserContext } from "../contexts/userContext";
import { useNotificationContext } from "../contexts/notificationContext";
import { getHeadersForCsv } from "../helpers/general";
import moment from 'helpers/moment';
import { useLocaleContext } from '../contexts/localeContext';

/* Components */
/* Styles */

export const useTravelCreditsService = () => {
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const intl = useIntl();
  const globalLocale = useLocaleContext();

  const [travelCreditsResponse, setTravelCreditsResponse] = useState({})

  const userDetails = useUserContext();

  const userEntityId = userDetails?.userInfo?.userEntityId;
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;


  const getTravelCreditsList = async (options, type, spinner) => {
    if (options) {
      const response = await genericApiFetch({
        url: TRAVEL_CREDITS_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{status}", type),
        method: "GET",
        queryParams: {...options},
        spinner: spinner
      });

      if (response.success) {
        setTravelCreditsResponse(response.data);
      } else {
        setTravelCreditsResponse({ results: null });
      }
    }
  };

  const unassignTripCredit = async (fopId) => {
    const response = await genericApiFetch({
      url: UNASSIGN_CREDITS_ENDPOINT.replace('{providerCode}', providerCode?.toLowerCase())
        .replace('{companyEntityId}', companyEntityId)
        .replace('{Id}', fopId),
      method: 'DELETE',
      spinner: 'unassignActionSpinner',
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.unassignTripCreditSuccessTitle" />,
        <FormattedMessage id="notification.unassignTripCreditSuccessText" />
      )
      return true;
    }
    return false;
  };

  const assignTravelCredit = async (groupData) => {    
    const response = await genericApiFetch({
      url: ASSIGN_TRAVEL_CREDITS_ENDPOINT.replace(
        '{providerCode}',
        providerCode?.toLowerCase(),
      ).replace('{companyEntityId}', companyEntityId),
      method: 'POST',
      data: groupData.requestBody,
      spinner: 'travelCreditsAssignSpinner',
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.assignTravelCredits.successTextTitle" />,
        <FormattedMessage id="notifications.assignTravelCredits.successTextDescription" />
      )
      return true;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.assignTravelCredits.errorTitle" />,
      <FormattedMessage id="notifications.assignTravelCredits.errorText" />
    )
    return false
  };

  const downloadTravelCredits = async (filters) => {
    delete filters["page"];    

    const response = await  genericApiFetch({
      url: TRAVELCREDITS_DOWNLOAD_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId).replace("{status}", filters.status),
      method: "GET",
      queryParams: filters,
      spinner: "downloadTravelCreditsSpinner",
    })
    if (response.success) {
      const customCsvData = response.data.items.map(item => {
        return {
          ...item,
          issueDateTime: item.issueDateTime && item.issueDateTime !== "0000-00-00 00:00:00"
          ? moment(item.issueDateTime).locale(globalLocale).format("DD MMM YYYY")
          : "-",
          expiryDate: item.expiryDate ? moment(item?.expiryDate).locale(globalLocale).format("DD MMM YYYY")  : "-",
          usedDate: item.usedDate ? moment(item?.usedDate).locale(globalLocale).format("DD MMM YYYY")  : "-",
          ticketNumber: item.ticketNumber != null ? `\u200C${item.ticketNumber}` : "",
          number: item.number != null ? `\u200C${item.number}` : ""
        };
      });
      csvDownload({
        data: customCsvData,
        filename: response.data?.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data?.items, "availableGrid", intl)
      });
      return true
    } else return false
  };

  const initialViewTravelCredits = async () => {
    const response = await genericApiFetch({
      url: TRAVELCREDITS_INITIAL_VIEW.replace("{userEntityId}", userEntityId),
      method: "PATCH",
      data: {}
    })
    if (response.success) {
      return response.data;
    };

    return false;
  }

  const tripCreditsSettings = async (assignmentType) => {
    const response = await genericApiFetch({
      url: TRIP_CREDIT_SETTINGS.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: {
        tripCreditsToggle: assignmentType
      },
      spinner: "tripDetailsSettingsSpinner"
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.tripCreditsSettings.successTextTitle" />,
        <FormattedMessage id="notifications.tripCreditsSettings.successTextDescription" />
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.removeDomainVerificationErrorTitle" />,
      <FormattedMessage id="notifications.removeDomainVerificationErrorText" />
    )
    return false;
  };

  const getCompanyAttributes = async () => {
    const response = await genericApiFetch({
      url: GET_COMPANY_ATTRIBUTES.replace("{companyEntityId}", companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  };

  const getTravelCreditsCount = async (status) => {
    const response = await genericApiFetch({
      url: TRAVEL_CREDIT_COUNT_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{status}", status),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  };

  return {
    getTravelCreditsList,
    travelCreditsResponse,
    unassignTripCredit,
    downloadTravelCredits,
    assignTravelCredit,
    initialViewTravelCredits,
    tripCreditsSettings,
    getCompanyAttributes,
    getTravelCreditsCount
  };
};
